import React, { useState, useImperativeHandle } from 'react';
import Html from 'components/html';

import { testId, sizes } from './desktop-carousel.settings';
import styles from './desktop-carousel.module.scss';

export interface DesktopCarouselProps {
  slides: Array<string>;
}

export interface DesktopCarouselHandle {
  setActiveSlide: (index: number) => void;
}

const DesktopCarousel = React.forwardRef<DesktopCarouselHandle, DesktopCarouselProps>((props, ref) => {
  const { slides } = props;
  const [activeIndex, setActiveIndex] = useState(0);

  // Slides are duplicated to create the infinite carousel effect
  const duplicatedSlides = React.useMemo(() => [...slides, ...slides], [slides]);

  useImperativeHandle(
    ref,
    () => ({
      setActiveSlide: (index: number) => {
        setActiveIndex(index);
      },
    }),
    []
  );

  const getSlideStyle = React.useCallback(
    (index: number): React.CSSProperties => {
      const position = index - activeIndex;

      if (position === 0) return {};

      const translateX =
        position > 0
          ? position * (sizes.inactive.width + sizes.spacing) + (sizes.active.width - sizes.inactive.width)
          : position * (sizes.inactive.width + sizes.spacing);
      const translateY = sizes.active.height - sizes.inactive.height;

      return { transform: `translate(${translateX}px, ${translateY}px)` } satisfies React.CSSProperties;
    },
    [activeIndex]
  );

  return (
    <Html.div className={styles.container} testId={testId.component}>
      {duplicatedSlides.map((slide, index) => {
        return (
          <Html.div
            // eslint-disable-next-line react/no-array-index-key
            key={`${slide}-${index}`}
            className={[styles.slide, activeIndex === index && styles.active]}
            style={getSlideStyle(index)}
            testId={testId.slide}
          >
            <img src={slide} alt={index.toString()} />
          </Html.div>
        );
      })}
    </Html.div>
  );
});

export default DesktopCarousel;
